/**
 * Geolocation Component
 *
 * No map library is currently installed, so we render the resolver's
 * `payload.geo.points` as a sortable bar list inside a shadcn `Card`.
 * Each row shows the geo key with a horizontal bar shaded by relative
 * value — useful as a stop-gap until a choropleth is wired in.
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { MapPin } from 'lucide-react';
import { useIndicatorData } from './useIndicatorData';

interface GeolocationComponentProps {
  indicatorId?: number;
}

export function GeolocationComponent({ indicatorId }: GeolocationComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);

  const points = useMemo(() => {
    const raw = live?.geo?.points || [];
    return [...raw].sort((a, b) => b.value - a.value);
  }, [live]);

  const max = useMemo(() => points.reduce((acc, p) => Math.max(acc, p.value), 0), [points]);

  if (loading && points.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
        No geolocation data.
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          By Location
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[280px] overflow-auto">
        {points.map((point) => {
          const ratio = max > 0 ? (point.value / max) * 100 : 0;
          return (
            <div key={point.key} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium truncate" title={point.key}>{point.key || '—'}</span>
                <span className="text-muted-foreground tabular-nums">{point.value.toLocaleString()}</span>
              </div>
              <div className="h-2 w-full rounded bg-muted">
                <div
                  className="h-full rounded bg-primary transition-all"
                  style={{ width: `${ratio}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
